import React, { FC } from 'react';
import classNames from 'classnames';

import type { RootMenuItem as RootMenuItemType } from '../../../../types';

import { Icon } from 'app/design-system/components/Icon';
import { ICON_NAME_WI_FI } from 'app/design-system/constants/IconNames';
import { useTranslation } from 'app/lang';
import { getSportIconUrl, getStreamIconUrl } from 'app/lib/betting/sport-utils';
import { getUniversalUrl } from 'app/lib/get-universal-url';
import { getDictionaryDataItems } from 'app/modules/Navigation/utils';

import dictionary from './LinkContent.dictionary.json';

import styles from '../../RootMenu.module.scss';

type LinkContentProps = {
  entry: RootMenuItemType;
};

const ICON_SIZE = 22;

export const LinkContent: FC<LinkContentProps> = ({
  entry,
}) => {
  const { t } = useTranslation();

  const matchesCount = 'entity' in entry ? entry.entity.matchesCount : 0;
  const isSport = entry.type === 'sport';
  const sportId = isSport ? entry?.sportId : undefined;
  const streamIconUrl = getStreamIconUrl(entry.key);
  const sportIconUrl = isSport && getUniversalUrl(getSportIconUrl(sportId));

  const iconClassNames = classNames(styles.icon, {
    [styles.iconWithBackground]: Boolean(sportId) || entry.key === 'streams',
  });

  const getImage = (src: string, alt: string) => {
    return (
      <span className={iconClassNames}>
        <img src={src}
             className={styles.icon}
             width={ICON_SIZE}
             height={ICON_SIZE}
             alt={alt} />
      </span>
    );
  };

  const getIconContent = (key: string) => {
    switch (key) {
      case 'live':
        return (
          <Icon icon={ICON_NAME_WI_FI}
                size={ICON_SIZE}
                className={iconClassNames} />
        );

      case 'streams':
        return <>{getImage(streamIconUrl, t(dictionary.streamsEntryAlt))}</>;

      default:
        return sportIconUrl && <>{getImage(sportIconUrl, t(dictionary.sportsEntryAlt))}</>;
    }
  };

  return (
    <>
      {getIconContent(entry.key)}

      {!entry.caption
        ? (
          <span className={styles.label}
                data-test="item-label">
            {getDictionaryDataItems(t, entry.label)}
          </span>
        )
        : (
          <>
            <span className={styles.label}
                  data-test="item-label">
              {getDictionaryDataItems(t, entry.label)}
            </span>
            <span className={styles.caption}
                  data-test="item-caption">
              {entry.caption}
            </span>
          </>
        )
      }

      {matchesCount > 0 && <span className={styles.matchesCount}>{matchesCount}</span>}
    </>
  );
};
